import React from 'react';
import { content, logoUrl } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Target, Eye, Heart, Quote } from 'lucide-react';

const About = () => {
  const { language } = useLanguage();
  const t = content[language]?.about || content.en.about;

  return (
    <section id="about" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-orange-50 text-orange-600 font-medium text-sm mb-4">
            {t.subtitle}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {t.title}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left - Image/Logo */}
          <div className="relative">
            <div className="relative bg-white rounded-3xl p-8 shadow-xl">
              <img 
                src={logoUrl} 
                alt="TransAfrica Canada" 
                className="w-full max-w-md mx-auto"
              />
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl opacity-20" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl opacity-20" />
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              {t.description}
            </p>

            {/* Mission, Vision, Values */}
            <div className="space-y-6">
              {/* Mission */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t.mission.title}</h3>
                  <p className="text-slate-600">{t.mission.text}</p>
                </div>
              </div>

              {/* Vision */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t.vision.title}</h3>
                  <p className="text-slate-600">{t.vision.text}</p>
                </div>
              </div>

              {/* Values */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">{t.values.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    {t.values.items.map((value, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm"
                      >
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Founders Message */}
        <div className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-3xl p-8 md:p-12 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl" />
          
          <div className="relative">
            <Quote className="w-12 h-12 text-blue-400/30 mb-4" />
            <p className="text-xl md:text-2xl text-white/90 italic leading-relaxed max-w-4xl">
              {t.foundersMessage}
            </p>
            <p className="text-slate-400 mt-6">
              — {language === 'fr' ? 'Les Fondateurs' : 'The Founders'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
